/**
 * ConfigureAssetDialog — the modal a user lands in after dropping files.
 *
 *   ┌─ Configure asset ─────────────────────────────────────┐
 *   │ rules.md   1.2 KB   sha256:cafebab…                   │
 *   │                                                       │
 *   │ Rules | Reference — subscription lane (catalog uses paired Hold-Rules / Hold-Ref )   │
 *   │                                                         │
 *   │ Mount points  [@Network:angry-dog ×] [+ Add mount]    │
 *   │                                                       │
 *   │                       [Cancel] [Save]                 │
 *   └───────────────────────────────────────────────────────┘
 *
 * Owns nothing about the network round-trip — it just collects user intent
 * and hands it back via ``onSave({ usageType, mounts })``. The catalog
 * caller orchestrates ``createUploadUrl → PUT → createAsset`` so this dialog
 * stays a pure form (testable, reusable on the org page).
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle } from 'lucide-react';
import MountTargetPicker from './MountTargetPicker.jsx';
import MemoryLanePairControl from './MemoryLanePairControl.jsx';

const USAGE_BY_VALUE = {
  RULES: {
    label: 'Directives (RULES)',
    desc: 'Short text injected directly into AI instructions.',
  },
  REFERENCE: {
    label: 'Knowledge (REFERENCE)',
    desc: 'Large documents searched dynamically by the agent.',
  },
};

// RULES injection budget (doc/arch/CONTEXT.md §6). RULES are
// always-injected, so a doc bigger than the budget is DROPPED WHOLE at run time.
// Kept in sync with the catalog + worker RULES_INJECTION_BUDGET_CHARS (SSM-tunable).
const RULES_INJECTION_BUDGET_BYTES = Number(
  import.meta.env?.VITE_RULES_INJECTION_BUDGET_CHARS || 48000,
);

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   onSave: (next: {
 *     usageType: 'RULES'|'REFERENCE',
 *     mounts: object[],
 *   }) => Promise<void> | void,
 *   prepared: { fileName: string, size: number, contentHash: string } | null,
 *   defaultUsageType?: 'RULES'|'REFERENCE',
 *   defaultMounts?: object[],
 *   networkId?: string,
 *   agentNames?: string[],
 *   poolIds?: string[],
 *   includeOrg?: boolean,
 *   mountSuggestMode?: string,
 *   reuseHint?: null | { priorUsageType?: 'RULES' | 'REFERENCE' | 'OFF' },
 * }} props
 */
export default function ConfigureAssetDialog({
  open,
  onClose,
  onSave,
  prepared,
  defaultUsageType = 'RULES',
  defaultMounts,
  networkId,
  agentNames,
  poolIds,
  includeOrg = false,
  mountSuggestMode,
  reuseHint = null,
}) {
  const [usageType, setUsageType] = useState(defaultUsageType);
  const [mounts, setMounts] = useState(defaultMounts || []);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Reset state every time the dialog re-opens. Without this, a user who
  // cancelled with `Knowledge` selected would see it pre-checked next time
  // even after dropping a different file.
  useEffect(() => {
    if (!open) return;
    const nextUt =
      defaultUsageType === 'REFERENCE' ? 'REFERENCE' : 'RULES';
    setUsageType(nextUt);

    /** Agentic picker modes never seed a ``@Network`` default. */
    if (mountSuggestMode) {
      setMounts(Array.isArray(defaultMounts) ? [...defaultMounts] : []);
    } else {
      setMounts(defaultMounts || (networkId ? [{ scope: 'NETWORK', networkId }] : []));
    }
    setSubmitting(false);
    setSubmitError(null);
  }, [open, defaultUsageType, defaultMounts, networkId, mountSuggestMode]);

  const onSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSave?.({
        usageType: usageType === 'REFERENCE' ? 'REFERENCE' : 'RULES',
        mounts,
      });
    } catch (e) {
      setSubmitError(e?.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape' && !submitting) onClose?.();
  };

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          key="configure-asset-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-40 flex items-start justify-center bg-black/40 p-4 sm:items-center"
          onClick={onClose}
          onKeyDown={onKeyDown}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="relative w-full max-w-md rounded-xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Configure data-plane asset"
          >
            <header className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <h2 className="text-sm font-semibold text-gray-900">Configure asset</h2>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                aria-label="Close"
                className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </header>

            <div className="space-y-4 px-4 py-3">
              {reuseHint?.priorUsageType != null ? (
                <div
                  role="note"
                  className="rounded-md border border-sky-100 bg-sky-50/90 px-3 py-2 text-[11px] leading-relaxed text-sky-950"
                >
                  Same bytes are already in this workspace{' '}
                  {reuseHint?.priorUsageType === 'REFERENCE'
                    ? '(REFERENCE)'
                    : reuseHint?.priorUsageType === 'RULES'
                      ? '(RULES)'
                      : '(on Hold — mount tags cleared until you subscribe again)'}
                  . Save adds any new mounts and applies your Rules vs Reference subscription
                  below. One ingestion mode applies per artifact; conflicting modes need a
                  different upload.
                </div>
              ) : null}

              <section>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Subscription (Rules vs Reference)
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <MemoryLanePairControl
                    variant="subscription"
                    idPrefix={`dp-usage-${(networkId || 'net').replace(/[^a-zA-Z0-9_-]/g, '_')}`}
                    value={usageType === 'REFERENCE' ? 'REFERENCE' : 'RULES'}
                    onChange={(v) => setUsageType(v)}
                    disabled={submitting}
                  />
                </div>
                <p className="mt-2 text-[11px] text-gray-600">
                  <span className="font-medium text-gray-700">
                    {USAGE_BY_VALUE[usageType]?.label ?? USAGE_BY_VALUE.RULES.label}
                  </span>
                  {' — '}
                  {USAGE_BY_VALUE[usageType]?.desc ?? USAGE_BY_VALUE.RULES.desc}
                </p>
                {usageType === 'RULES'
                && Number(prepared?.size || 0) > RULES_INJECTION_BUDGET_BYTES ? (
                  <div
                    className="mt-2 flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-800"
                    role="alert"
                    data-testid="dp-configure-rules-oversize"
                  >
                    <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
                    <span>
                      This file ({formatBytes(prepared?.size || 0)}) is far larger than the
                      RULES injection budget (~{formatBytes(RULES_INJECTION_BUDGET_BYTES)}).
                      RULES are always-injected, so it will be{' '}
                      <span className="font-semibold">dropped whole at run time</span>{' '}
                      (never truncated). For a document this size, switch to{' '}
                      <button
                        type="button"
                        onClick={() => setUsageType('REFERENCE')}
                        className="font-semibold underline hover:text-amber-900"
                        disabled={submitting}
                      >
                        REFERENCE
                      </button>{' '}
                      — it's retrieved on demand instead of injected.
                    </span>
                  </div>
                ) : null}
              </section>

              {prepared ? (
                <section>
                  <div className="text-xs font-medium text-gray-700">{prepared.fileName}</div>
                  <div className="text-[11px] text-gray-500">
                    {formatBytes(prepared.size)} · {short(prepared.contentHash)}
                  </div>
                </section>
              ) : null}

              <section>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Mount points
                </div>
                <MountTargetPicker
                  value={mounts}
                  onChange={setMounts}
                  networkId={networkId}
                  agentNames={agentNames}
                  poolIds={poolIds}
                  includeOrg={includeOrg}
                  mountSuggestMode={mountSuggestMode}
                  disabled={submitting}
                />
                <p className="mt-1 text-[11px] text-gray-500">
                  Tag where this file is applied at runtime — required before Save. Moving an
                  artifact to Hold in the grid clears mounts so new tags are required next time you
                  activate.
                </p>
              </section>

              {submitError ? (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700">
                  {submitError}
                </div>
              ) : null}
            </div>

            <footer className="flex items-center justify-end gap-2 border-t border-gray-100 px-4 py-3">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-2.5 py-1 text-xs font-medium rounded-md text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onSubmit}
                disabled={submitting || mounts.length === 0}
                className="px-2.5 py-1 text-xs font-medium rounded-md bg-blue-600 text-white transition-colors hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
              >
                {submitting ? 'Saving…' : 'Save'}
              </button>
            </footer>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

function formatBytes(n) {
  if (!Number.isFinite(n)) return `${n} B`;
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function short(hash) {
  if (!hash) return '';
  const colon = hash.indexOf(':');
  return colon >= 0
    ? `${hash.slice(0, colon + 1)}${hash.slice(colon + 1, colon + 9)}…`
    : `${hash.slice(0, 12)}…`;
}
